import React, { useState, useEffect } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import { logsAnalysisService } from '../services/logsAnalysisService';
import { Select } from 'antd';
import { useTheme } from '../context/ThemeContext';

const TIME_RANGE_LABEL = {
  '24h': 'Last 24h',
  '7d': 'Last 7d',
  '30d': 'Last 30d',
};

const parseUsd = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const numeric = Number(String(value).replace(/[^0-9.-eE]+/g, ''));
  return Number.isFinite(numeric) ? numeric : 0;
};

const parseVolumeMb = (value) => {
  if (!value) return 0;
  const match = String(value).match(/(\d+\.?\d*)/);
  return match ? parseFloat(match[1]) : 0;
};

const formatVolumeTick = (value) => {
  const numeric = Number(value) || 0;
  if (numeric === 0) return '0';
  if (numeric < 0.001) return numeric.toFixed(4);
  if (numeric < 0.01) return numeric.toFixed(3);
  if (numeric < 1) return numeric.toFixed(2);
  return numeric.toFixed(1);
};

const shortenLabel = (value, maxLength = 26) => {
  const text = String(value || '').trim();
  if (text.length <= maxLength) return text;
  const keepStart = Math.ceil((maxLength - 1) / 2);
  const keepEnd = Math.floor((maxLength - 1) / 2);
  return `${text.slice(0, keepStart)}…${text.slice(-keepEnd)}`;
};

const addVolumeRow = (rowsByName, name, value) => {
  const label = String(name || '').trim();
  if (!label) return;
  const normalized = label.toLowerCase();
  if (normalized === 'unknown' || normalized === 'unknown_service' || normalized === 'error') return;

  rowsByName.set(label, (rowsByName.get(label) || 0) + parseVolumeMb(value));
};

const buildCombinedLogVolumeRows = (combinedData) => {
  const rowsByName = new Map();
  const bucketRows = Array.isArray(combinedData?.volume_by_bucket)
    ? combinedData.volume_by_bucket
    : [];
  const serviceRows = Array.isArray(combinedData?.volume_by_service)
    ? combinedData.volume_by_service
    : [];
  const sourceRows = Array.isArray(combinedData?.volume_by_source)
    ? combinedData.volume_by_source
    : [];
  const logGroupRows = Array.isArray(combinedData?.volume_by_log_group)
    ? combinedData.volume_by_log_group
    : Array.isArray(combinedData?.volume_by_loggroup)
    ? combinedData.volume_by_loggroup
    : [];
  const hasNamedBuckets = bucketRows.length > 0;

  // Process log groups
  logGroupRows.forEach((item) => {
    const logGroupName = String(item.metric?.log_group ?? item.metric?.loggroup ?? item.metric?.service_name ?? '').trim();
    if (!logGroupName) return;
    addVolumeRow(rowsByName, logGroupName, item.value?.[1]);
  });

  // Process services
  serviceRows.forEach((item) => {
    const serviceName = String(item.metric?.log_group ?? item.metric?.loggroup ?? item.metric?.service_name ?? item.metric?.service ?? '').trim();
    if (!serviceName) return;
    if (hasNamedBuckets && serviceName.toUpperCase() === 'S3') return;
    
    const normalized = serviceName.toLowerCase();
    for (const existingKey of rowsByName.keys()) {
      if (existingKey.toLowerCase() === normalized) return;
    }
    
    addVolumeRow(rowsByName, serviceName, item.value?.[1]);
  });

  // Process buckets, but avoid double counting if the service name matches the bucket exactly
  // bucketRows.forEach((item) => {
  //   const bucketName = String(item.metric?.s3_bucket ?? item.metric?.bucket ?? '').trim();
  //   if (!bucketName) return;
    
  //   const normalizedBucket = bucketName.toLowerCase();
  //   for (const existingKey of rowsByName.keys()) {
  //     if (existingKey.toLowerCase() === normalizedBucket) return;
  //   }
    
  //   addVolumeRow(rowsByName, `s3://${bucketName}`, item.value?.[1]);
  // });
  bucketRows.forEach((item) => {
    const bucketName = String(
      item.metric?.s3_bucket ??
      item.metric?.bucket ??
      ''
    ).trim();

    if (!bucketName) return;

    const displayName = `s3://${bucketName}`;

    const normalizedBucket = displayName.toLowerCase();

    for (const existingKey of rowsByName.keys()) {
      if (existingKey.toLowerCase() === normalizedBucket) return;
    }

    addVolumeRow(rowsByName, displayName, item.value?.[1]);
  });
  // Fallback to sources if no granular breakdown exists
  sourceRows.forEach((item) => {
    const sourceName = String(item.metric?.source ?? '').trim();
    if (!sourceName) return;

    if (sourceName.toLowerCase() === 's3') {
      const hasS3Data = Array.from(rowsByName.keys()).some(k => k.toLowerCase() === 's3');
      if (!hasS3Data && !hasNamedBuckets) {
        addVolumeRow(rowsByName, 'S3', item.value?.[1]);
      }
    } else if (sourceName.toLowerCase() === 'cloudwatch') {
      if (rowsByName.size === 0) {
        addVolumeRow(rowsByName, 'CloudWatch', item.value?.[1]);
      }
    }
  });

  return Array.from(rowsByName.entries())
    .map(([name, volume]) => ({ name, volume }))
    .sort((a, b) => b.volume - a.volume)
    .slice(0, 10);
};

const LogVolumeTimeline = ({ accountId }) => {
  const { isDark } = useTheme();
  const [serviceData, setServiceData] = useState([]);
  const [processingCost, setProcessingCost] = useState({
    total: 0,
    request: 0,
    compute: 0,
  });
  const [costAvailable, setCostAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setServiceData([]);
        setProcessingCost({ total: 0, request: 0, compute: 0 });
        setCostAvailable(false);
        setErrorMessage('');

        const selectedAccount = accountId && accountId !== '__all__' ? accountId : 'all';

        const [combinedAnalysisResult, costResult] = await Promise.allSettled([
          logsAnalysisService.getServiceAnalysis('combined', timeRange, selectedAccount),
          logsAnalysisService.getLogCostAnalysis({
            label: 'account_id',
            timerange: timeRange,
            account_id: selectedAccount || 'all',
            lambda_function_name: 'clairai-logs-to-loki-lambda',
          }),
        ]);

        const combinedAnalysisData = combinedAnalysisResult.status === 'fulfilled'
          ? combinedAnalysisResult.value
          : null;
        if (costResult.status === 'fulfilled') {
          const payload = costResult.value || {};
          const components = payload.cost_components || {};
          setProcessingCost({
            total: parseUsd(payload.total_cost),
            request: parseUsd(components.request_cost_total_usd),
            compute: parseUsd(components.compute_cost_total_usd),
          });
          setCostAvailable(true);
        }

        const services = buildCombinedLogVolumeRows(combinedAnalysisData);
        setServiceData(services);
      } catch (error) {
        console.error('Error fetching service data:', error);
        setErrorMessage(error.message || 'Failed to fetch log processing data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, accountId]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: isDark ? '#adbac7' : '#888' }}>Loading...</div>;
  }

  if (serviceData.length === 0) {
    const hasError = !!errorMessage;
    const isUnavailable = hasError && (
      errorMessage.toLowerCase().includes('failed') ||
      errorMessage.toLowerCase().includes('500') ||
      errorMessage.toLowerCase().includes('request')
    );
    return (
      <div style={{
        padding: '20px 24px',
        background: isDark ? '#161b22' : '#f8fafc',
        borderRadius: 8,
        border: isDark ? '1px solid #30363d' : '1px solid #e2e8f0',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '12px', color: isDark ? '#8b949e' : '#64748b', marginBottom: 4 }}>
          {TIME_RANGE_LABEL[timeRange] || 'Last 24h'}
        </div>
        <div style={{ fontSize: '13px', fontWeight: 500, color: isDark ? '#e6edf3' : '#0f172a', marginBottom: 6 }}>
          {hasError && isUnavailable
            ? 'Log processing data is not available.'
            : 'Logs processed insights will populate once ingestion is active.'}
        </div>
        <div style={{ fontSize: '12px', color: isDark ? '#8b949e' : '#64748b', marginBottom: 12 }}>
          {hasError && isUnavailable
            ? 'No processed log data has been aggregated yet for this account, or the service is temporarily unavailable.'
            : 'Connect log sources to start processing logs into Loki.'}
        </div>
        <Link to="/logs" style={{ fontSize: '12px', color: isDark ? '#58a6ff' : '#0969da' }}>Connect log sources</Link>
      </div>
    );
  }

  const colors = ['#ef4444', '#06b6d4', '#22c55e', '#a855f7'];
  const chartData = serviceData.map((service, index) => ({
    name: service.name,
    shortName: shortenLabel(service.name),
    volume: service.volume,
    fill: colors[index % colors.length],
  }));
  
  const total = serviceData.reduce((sum, s) => sum + s.volume, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
        <Select value={timeRange} onChange={setTimeRange} size="small" style={{ width: '120px' }}>
          <Select.Option value="24h">Last 24 Hours</Select.Option>
          <Select.Option value="7d">Last 7 Days</Select.Option>
          <Select.Option value="30d">Last 30 Days</Select.Option>
        </Select>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: isDark ? '#8b949e' : '#64748b' }}>
            Estimated Loki log processing ({TIME_RANGE_LABEL[timeRange] || timeRange})
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: isDark ? '#e6edf3' : '#0f172a' }}>
            {costAvailable ? `$${processingCost.total.toFixed(6)}` : 'Not available'}
          </div>
          <div style={{ fontSize: 11, color: isDark ? '#8b949e' : '#64748b' }}>
            {costAvailable
              ? `Request: $${processingCost.request.toFixed(6)} | Compute: $${processingCost.compute.toFixed(6)}`
              : 'Cost components unavailable'}
          </div>
        </div>
      </div>
      <div style={{ height: '140px', marginBottom: '8px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: 8, left: 4, bottom: 4 }}
            barCategoryGap={10}
          >
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fill: isDark ? '#adbac7' : '#64748b', fontSize: 11 }}
              tickFormatter={formatVolumeTick}
            />
            <YAxis
              type="category"
              dataKey="shortName"
              axisLine={false}
              tickLine={false}
              tick={{ fill: isDark ? '#adbac7' : '#64748b', fontSize: 11 }}
              width={150}
            />
            <Tooltip
              cursor={{ fill: isDark ? 'rgba(110, 118, 129, 0.08)' : 'rgba(148, 163, 184, 0.12)' }}
              contentStyle={{
                background: isDark ? '#1c2128' : '#fff',
                border: isDark ? '1px solid #30363d' : '1px solid #e2e8f0',
                borderRadius: 6,
                fontSize: 11,
                padding: '6px 10px',
                color: isDark ? '#e6edf3' : '#000'
              }}
              formatter={(value, _name, item) => [
                `${Number(value).toFixed(4)} MB`,
                item?.payload?.name || 'Processed volume',
              ]}
              labelFormatter={(_label, payload) => payload?.[0]?.payload?.name || ''}
            />
            <Bar dataKey="volume" radius={[0, 6, 6, 0]} maxBarSize={18}>
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {serviceData.map((service, index) => {
          const percentage = total > 0 ? ((service.volume / total) * 100).toFixed(1) : 0;
          return (
            <div key={service.name} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: isDark ? '#adbac7' : '#64748b' }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '2px',
                background: colors[index % colors.length]
              }}></div>
              {service.name} ({percentage}%)
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LogVolumeTimeline;
